import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDPagination from 'components/MDPagination';
import Icon from '@mui/material/Icon';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import { apiGetGrindHist, apiGetAllGrindHist } from '../../api/stats.api';
import { useData } from '../../hooks';
import TopNavbar from 'examples/Navbars/TopNavbar';
import { handleDownloadExcel } from '../../components/excel/excelUtils';
import MDButton from '../../components/MDButton';
import { Snackbar, Alert } from '@mui/material';

const Grinds = () => {
  const [grinds, setGrinds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sumPetCnt, setSumPetCnt] = useState(0);
  const [branchId, setBranchId] = useState(null);
  const [machineId, setMachineId] = useState(null);
  const [userId, setUserId] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { user, appIsReady } = useData();

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setCreatedFrom(today);
    setCreatedTo(today);
  }, []);

  useEffect(() => {
    if (createdFrom && createdTo && appIsReady && user) {
      fetchGrinds(page);
    }
  }, [page, user, appIsReady, createdFrom, createdTo]);

  const fetchGrinds = async (pageNum) => {
    setLoading(true);
    try {
      const response = await apiGetGrindHist(
        pageNum,
        10,
        branchId,
        machineId,
        createdFrom,
        createdTo,
        userId,
        user.accessToken,
      );
      if (response && response.data) {
        setGrinds(response.data.data);
        setTotal(response.data.total);
        setSumPetCnt(response.data.sum_pet_cnt);
      } else {
        console.log('No data received');
      }
    } catch (error) {
      console.error('Failed to load grinds:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNum) => {
    setPage(pageNum);
  };

  const handleSearch = () => {
    fetchGrinds(1);
    setPage(1);
  };

  const handleInputChange = (e, setter) => {
    const value = e.target.value;
    if (value === '' || /^[0-9]+$/.test(value)) {
      setter(Number(value));
      setErrorMessage('');
    } else {
      setErrorMessage('숫자만 입력할 수 있습니다.');
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  /**
   * 전체 데이터를 가져와서 엑셀 다운로드하는 함수
   * 현재 설정된 필터 조건을 적용하여 모든 페이지의 데이터를 가져옴
   */
  const handleDownloadAllExcel = async () => {
    if (!user || !user.accessToken) {
      setErrorMessage('사용자 인증 정보가 없습니다.');
      setOpenSnackbar(true);
      return;
    }

    setIsDownloading(true);
    try {
      // 전체 데이터 조회
      const response = await apiGetAllGrindHist(
        branchId,
        machineId,
        createdFrom,
        createdTo,
        userId,
        user.accessToken,
      );

      if (response && response.data && response.data.data) {
        // 전체 데이터를 엑셀 형식으로 변환
        const allRows = response.data.data.map((grind) => ({
          id: grind.id,
          branch_name: grind.branch.branch_name,
          machine_id: grind.machine_id,
          device_name: grind.petmachine.device_name,
          userid: grind.userid,
          phone: grind.phone,
          pet_cnt: grind.pet_cnt,
          createdAt: formatDate(grind.createdAt),
          status: grind.status,
        }));

        // 엑셀 다운로드 실행
        await handleDownloadExcel('분쇄량_전체데이터', columns, allRows);

        console.log(
          `전체 ${allRows.length}건의 데이터를 엑셀로 다운로드했습니다.`,
        );
      } else {
        setErrorMessage('다운로드할 데이터가 없습니다.');
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error('엑셀 다운로드 중 오류 발생:', error);
      setErrorMessage('엑셀 다운로드 중 오류가 발생했습니다.');
      setOpenSnackbar(true);
    } finally {
      setIsDownloading(false);
    }
  };

  const formatDate = (dateString) => {
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    };
    return new Date(dateString)
      .toLocaleString('ko-KR', options)
      .replace(/. /g, '-')
      .replace('.', '')
      .replace(' ', ' ');
  };

  const rows = grinds.map((grind) => ({
    id: grind.id,
    branch_name: grind.branch.branch_name,
    machine_id: grind.machine_id,
    device_name: grind.petmachine.device_name,
    userid: grind.userid,
    phone: grind.phone,
    pet_cnt: grind.pet_cnt,
    createdAt: formatDate(grind.createdAt),
    status: grind.status,
  }));

  const columns = [
    { Header: 'ID', accessor: 'id', align: 'left' },
    { Header: '지점명', accessor: 'branch_name', align: 'left' },
    { Header: 'Machine ID', accessor: 'machine_id', align: 'left' },
    { Header: '기기명', accessor: 'device_name', align: 'left' },
    { Header: '사용자ID', accessor: 'userid', align: 'left' },
    { Header: 'Phone', accessor: 'phone', align: 'left' },
    { Header: '수량', accessor: 'pet_cnt', align: 'left' },
    { Header: '분쇄일시', accessor: 'createdAt', align: 'left' },
    { Header: '상태', accessor: 'status', align: 'left' },
  ];

  const totalPages = Math.ceil(total / 10);
  const maxPageNumbers = 10;
  const currentPageGroup = Math.floor((page - 1) / maxPageNumbers);
  const startPage = currentPageGroup * maxPageNumbers + 1;
  const endPage = Math.min(startPage + maxPageNumbers - 1, totalPages);
  const pageNumbers = Array.from(
    { length: endPage - startPage + 1 },
    (_, index) => startPage + index,
  );

  return (
    <DashboardLayout>
      <TopNavbar />
      <MDBox display='flex' mr={2} justifyContent='right' gap={2}>
        <MDButton
          variant='gradient'
          color='secondary'
          onClick={() =>
            handleDownloadExcel('분쇄량_현재페이지', columns, rows)
          }
          sx={{ ml: 2 }}
        >
          현재 페이지 엑셀 다운로드
        </MDButton>
        <MDButton
          variant='gradient'
          color='info'
          onClick={handleDownloadAllExcel}
          disabled={isDownloading}
          sx={{ ml: 2 }}
        >
          {isDownloading ? '다운로드 중...' : '전체 데이터 엑셀 다운로드'}
        </MDButton>
      </MDBox>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant='gradient'
                bgColor='info'
                borderRadius='lg'
                coloredShadow='info'
                display='flex'
                justifyContent='space-between'
                alignItems='center'
              >
                <MDTypography variant='h6' color='white'>
                  분쇄량
                </MDTypography>
                <MDTypography variant='h6' color='white'>
                  총Pet수: {sumPetCnt}
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          label='Branch ID'
                          fullWidth
                          value={branchId ?? ''}
                          onChange={(e) => handleInputChange(e, setBranchId)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          label='Machine ID'
                          fullWidth
                          value={machineId ?? ''}
                          onChange={(e) => handleInputChange(e, setMachineId)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          label='User ID'
                          fullWidth
                          value={userId}
                          onChange={(e) => setUserId(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          label='분쇄일 From'
                          type='date'
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={createdFrom}
                          onChange={(e) => setCreatedFrom(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          label='분쇄일 To'
                          type='date'
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={createdTo}
                          onChange={(e) => setCreatedTo(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Button
                          variant='contained'
                          color='success'
                          fullWidth
                          onClick={handleSearch}
                        >
                          검색
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  loading={loading}
                />
              </MDBox>
              <MDBox
                display='flex'
                flexDirection={{ xs: 'column', sm: 'row' }}
                justifyContent='center'
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                p={3}
              >
                <MDPagination variant='gradient' color='info'>
                  {page > 1 && (
                    <MDPagination
                      item
                      onClick={() => handlePageChange(page - 1)}
                    >
                      <Icon sx={{ fontWeight: 'bold' }}>chevron_left</Icon>
                    </MDPagination>
                  )}
                  {pageNumbers.map((pageNum) => (
                    <MDPagination
                      item
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      active={page === pageNum}
                    >
                      {pageNum}
                    </MDPagination>
                  ))}
                  {page < totalPages && (
                    <MDPagination
                      item
                      onClick={() => handlePageChange(page + 1)}
                    >
                      <Icon sx={{ fontWeight: 'bold' }}>chevron_right</Icon>
                    </MDPagination>
                  )}
                </MDPagination>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity='error'
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default Grinds;
